import React, { useState } from 'react'
import { ContextNotification } from '@pandiafi/components'
import { Button } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'

import { equipmentLabels, formatList } from './uiSelectors'
import { JoinQueueForm } from './JoinQueueForm'
import { getJoinableQueueEquipmentTypes } from './parkingSpots.helpers'
import type { CoreParkingSpot } from '../../../common/validations/contractParkingSpotsSchema'

import { useModuleConfig } from 'hooks/useModuleConfig/useModuleConfig'
import { useAPIDeleteParkingQueueItem } from 'api/apiHooks/useAPIDeleteParkingQueueItem'
import { QueueEntry } from 'api/apiHooks/useAPIGetParkingQueue'

function QueueList({
  items,
  onDelete,
  busyId,
  isDeleting,
  onJoinClick,
  canJoinQueue,
}: {
  items: QueueEntry[]
  onDelete: (id: number) => void
  busyId?: number | null
  isDeleting: boolean
  onJoinClick: () => void
  canJoinQueue: boolean
}) {
  const { t } = useTranslation('parking')

  if (!items.length) {
    return (
      <ContextNotification
        type="info"
        className="mb-4"
        action={
          canJoinQueue ? (
            <Button small onClick={onJoinClick}>
              {t('queue.join')}
            </Button>
          ) : undefined
        }
      >
        {t('queue.empty')}
      </ContextNotification>
    )
  }

  return (
    <ul className="mb-4">
      {items.map((it) => {
        const labels = equipmentLabels(it.equipmentWishes, t)
        const featuresText = formatList(labels)

        return (
          <li key={it.id}>
            <ContextNotification
              type="info"
              action={
                <Button
                  small
                  onClick={() => onDelete(it.id)}
                  disabled={busyId === it.id || isDeleting}
                >
                  {t('queue.leave')}
                </Button>
              }
            >
              {t('queue.youAreWaiting', { features: featuresText })}
              {it.additionalInformation
                ? t('queue.additionalInfoSuffix', {
                    info: it.additionalInformation,
                  })
                : ''}
            </ContextNotification>
          </li>
        )
      })}
    </ul>
  )
}

type Props = {
  parkingSpots: CoreParkingSpot[]
  queueItems?: QueueEntry[]
  costPoolId: number
  userMetadata: {
    isImpersonator: boolean
  }
}

export const ParkingQueuePanel: React.FC<Props> = ({
  parkingSpots,
  queueItems,
  costPoolId,
  userMetadata,
}) => {
  const { parkingSpots: parkingSpotsConfig } = useModuleConfig()
  const del = useAPIDeleteParkingQueueItem()
  const [showJoinModal, setShowJoinModal] = useState(false)

  const items: QueueEntry[] = (queueItems ?? []).filter(
    (it) => it.kind === 'own'
  )
  const joinableEquipment = getJoinableQueueEquipmentTypes(
    parkingSpots,
    queueItems ?? []
  )
  const canJoinQueue = joinableEquipment.length > 0

  const onDelete = async (id: number) => {
    await del.mutateAsync({ queueId: id, costPoolId })
  }

  const deleteBusyId: number | null =
    typeof del.variables === 'object' && del.variables !== null
      ? del.variables.queueId
      : null

  if (!parkingSpotsConfig.showQueue) {
    return null
  }

  return (
    <div>
      <section className="mb-4">
        <QueueList
          items={items}
          onDelete={onDelete}
          busyId={deleteBusyId}
          isDeleting={del.isPending}
          onJoinClick={() => setShowJoinModal(true)}
          canJoinQueue={canJoinQueue}
        />
      </section>

      <JoinQueueForm
        show={showJoinModal}
        onClose={() => setShowJoinModal(false)}
        costPoolId={costPoolId}
        parkingSpots={parkingSpots}
        queueItems={queueItems ?? []}
        userMetadata={userMetadata}
      />
    </div>
  )
}
